import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, Trash2, Edit3 } from 'lucide-react';
import { colors, API, fmt, authHeaders } from '../utils/theme';
import {
  PageHeader, SearchBar, Select, Table, Th, Td, TrHover, Badge,
  Modal, FormField, Input, TextArea, Button, LoadingSpinner, EmptyState,
  ConfirmDialog,
} from '../components/UI';

const STATUS_ANUIDADE = ['ATIVO', 'QUITADO', 'CANCELADO', 'ISENTO'];

export default function CriseAnuidadesPage() {
  const mesAtual = new Date().toISOString().slice(0, 7);
  const [anuidades,     setAnuidades]     = useState([]);
  const [autoAnuidades, setAutoAnuidades] = useState([]);
  const [stats,         setStats]         = useState({});
  const [cartoes,       setCartoes]       = useState([]);
  const [titulares,     setTitulares]     = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [filtros,       setFiltros]       = useState({ search: '', status: '', mes: '', titular_id: '' });
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const emptyForm = {
    cartao_id: '', valor_total: '', num_parcelas: '12', valor_parcela: '', parcelas_pagas: '0',
    data_inicio: '', data_fim: '', isento: 'NÃO', motivo_isencao: '', status: 'ATIVO', observacoes: '',
  };
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (filtros.status) params.set('status', filtros.status);
    const autoParams = new URLSearchParams();
    if (filtros.mes) autoParams.set('mes', filtros.mes);
    if (filtros.titular_id) autoParams.set('titular_id', filtros.titular_id);
    try {
      const [an, st, auto] = await Promise.all([
        fetch(`${API}/crise/anuidades?${params}`).then(r => r.json()),
        fetch(`${API}/crise/anuidades/stats`).then(r => r.json()),
        fetch(`${API}/crise/custos/anuidades?${autoParams}`).then(r => r.json()),
      ]);
      setAnuidades(Array.isArray(an) ? an : []);
      setStats(st || {});
      setAutoAnuidades(Array.isArray(auto) ? auto : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filtros]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    fetch(`${API}/cartoes`, { headers: authHeaders() }).then(r => r.json()).then(d => setCartoes(Array.isArray(d) ? d : [])).catch(() => {});
    fetch(`${API}/titulares`, { headers: authHeaders() }).then(r => r.json()).then(d => setTitulares((d.data||d||[]).filter(t => t.total_cartoes > 0))).catch(() => {});
  }, []);

  const openCreate = () => { setEditId(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (a) => {
    setEditId(a.id);
    const f = {};
    Object.keys(emptyForm).forEach(k => { f[k] = a[k] != null ? String(a[k]) : emptyForm[k]; });
    setForm(f);
    setModalOpen(true);
  };

  // Auto-calc valor_parcela
  const calcParcela = (field, value) => {
    const newForm = { ...form, [field]: value };
    const vt = parseFloat(newForm.valor_total) || 0;
    const np = parseInt(newForm.num_parcelas) || 1;
    if (vt > 0 && np > 0 && field !== 'valor_parcela') {
      newForm.valor_parcela = (vt / np).toFixed(2);
    }
    setForm(newForm);
  };

  const save = async () => {
    if (!form.cartao_id) return alert('Cartão é obrigatório');
    setSaving(true);
    try {
      const method = editId ? 'PUT' : 'POST';
      const url = editId ? `${API}/crise/anuidades/${editId}` : `${API}/crise/anuidades`;
      await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      setModalOpen(false); load();
    } catch (e) { alert('Erro: ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await fetch(`${API}/crise/anuidades/${deleteId}`, { method: 'DELETE' });
      setDeleteId(null); load();
    } catch (e) { alert('Erro: ' + e.message); }
  };

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const autoFiltrado = autoAnuidades.filter(r => {
    const q = (filtros.search || '').toLowerCase();
    return !q || r.nome_cartao?.toLowerCase().includes(q) || r.titular_nome?.toLowerCase().includes(q) || r.descricao?.toLowerCase().includes(q);
  });
  const totalAutoAnuidades = autoFiltrado.reduce((s, r) => s + (r.valor || 0), 0);

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader title="Anuidades"
        subtitle={`${autoFiltrado.length} cobranças detectadas automaticamente`}
        onAdd={openCreate} addLabel="+ Manual" />

      {/* KPIs */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        {[
          { label: 'Total anuidades (auto)', value: fmt(totalAutoAnuidades), color: colors.danger, sub: `${autoFiltrado.length} cobranças` },
          { label: 'Anuidades manuais ativas', value: stats.ativas || 0, color: colors.warning, sub: fmt(stats.valor_total_anuidades) },
          { label: 'Isentas', value: stats.isentas || 0, color: colors.success, sub: 'negociadas' },
        ].map((kpi, i) => (
          <div key={i} style={{ background: colors.card, borderRadius: 12, padding: 16, border: `1px solid ${colors.border}`, flex: '1 1 160px' }}>
            <div style={{ fontSize: 11, color: colors.textMuted }}>{kpi.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
            <div style={{ fontSize: 11, color: colors.textDim }}>{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <SearchBar value={filtros.search} onChange={e => setFiltros(f => ({ ...f, search: e.target.value }))}
        placeholder="Buscar cartão, titular, descrição...">
        <Select value={filtros.titular_id} onChange={e => setFiltros(f => ({ ...f, titular_id: e.target.value }))} style={{ width: 'auto', minWidth: 160 }}>
          <option value="">Todos Titulares</option>
          {titulares.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </Select>
        <Input type="month" value={filtros.mes} onChange={e => setFiltros(f => ({ ...f, mes: e.target.value }))} style={{ width: 'auto', minWidth: 160 }} />
        <button onClick={() => setFiltros(f => ({ ...f, mes: '' }))}
          style={{ background: 'none', border: `1px solid ${colors.border}`, borderRadius: 6, padding: '6px 10px', color: colors.textMuted, cursor: 'pointer', fontSize: 12 }}>
          Todos meses
        </button>
      </SearchBar>

      {/* Automático */}
      <div style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
        <CreditCard size={14} color={colors.info} />
        <span style={{ fontSize: 12, fontWeight: 600, color: colors.info }}>Detectado automaticamente das faturas — todos os cartões</span>
      </div>

      {autoFiltrado.length === 0 ? (
        <EmptyState message="Nenhuma anuidade detectada nas faturas importadas para este período" icon={CreditCard} />
      ) : (
        <Table>
          <thead><tr>
            <Th>Mês</Th><Th>Data</Th><Th>Cartão</Th><Th>Titular</Th><Th>Banco</Th>
            <Th>Descrição</Th><Th>Valor</Th>
          </tr></thead>
          <tbody>
            {autoFiltrado.map(r => (
              <TrHover key={r.id} style={{ borderLeft: r.gerenciamento_crise === 'SIM' ? '3px solid #f97316' : undefined }}>
                <Td style={{ fontSize: 11, color: colors.textMuted }}>{r.mes_referencia}</Td>
                <Td style={{ fontSize: 11, color: colors.textMuted }}>{r.data_compra || '-'}</Td>
                <Td style={{ fontWeight: 600, fontSize: 12 }}>
                  {r.nome_cartao}
                  {r.gerenciamento_crise === 'SIM' && (
                    <span style={{ fontSize: 9, background: '#f9731622', color: '#f97316', borderRadius: 3, padding: '1px 4px', marginLeft: 4 }}>CRISE</span>
                  )}
                </Td>
                <Td style={{ fontSize: 12 }}>{r.titular_nome}</Td>
                <Td style={{ fontSize: 11, color: colors.textMuted }}>{r.banco_nome || '-'}</Td>
                <Td style={{ fontSize: 12 }}>{r.descricao}</Td>
                <Td style={{ fontWeight: 700, color: colors.danger }}>{fmt(r.valor)}</Td>
              </TrHover>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: `2px solid ${colors.border}` }}>
              <td colSpan={6} style={{ padding: '8px 12px', fontWeight: 600, color: colors.textMuted, fontSize: 12 }}>Total</td>
              <td style={{ padding: '8px 12px', fontWeight: 700, color: colors.danger }}>{fmt(totalAutoAnuidades)}</td>
            </tr>
          </tfoot>
        </Table>
      )}

      {/* Anuidades manuais */}
      {anuidades.length > 0 && (
        <>
          <div style={{ marginTop: 28, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: colors.textMuted }}>Anuidades manuais registradas</span>
            <Select value={filtros.status} onChange={e => setFiltros(f => ({ ...f, status: e.target.value }))} style={{ width: 'auto', minWidth: 140, marginLeft: 'auto' }}>
              <option value="">Todos Status</option>
              {STATUS_ANUIDADE.map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
          </div>
          <Table>
            <thead><tr>
              <Th>Cartão</Th><Th>Titular</Th><Th>Valor Total</Th><Th>Parcelas</Th><Th>Pagas</Th><Th>Isento</Th><Th>Status</Th><Th align="center">Ações</Th>
            </tr></thead>
            <tbody>
              {anuidades.map(a => (
                <TrHover key={a.id}>
                  <Td style={{ fontWeight: 600, fontSize: 12 }}>{a.nome_cartao}</Td>
                  <Td style={{ fontSize: 12 }}>{a.titular_nome}</Td>
                  <Td style={{ fontWeight: 600 }}>{a.isento === 'SIM' ? <span style={{ color: colors.success }}>ISENTO</span> : <span style={{ color: colors.danger }}>{fmt(a.valor_total)}</span>}</Td>
                  <Td>{a.num_parcelas}x / {fmt(a.valor_parcela)}</Td>
                  <Td>{a.parcelas_pagas}/{a.num_parcelas}</Td>
                  <Td>{a.isento === 'SIM' ? <Badge text="ISENTO" color={colors.success} /> : <span style={{ color: colors.textDim }}>Não</span>}</Td>
                  <Td><Badge text={a.status} color={a.status === 'ATIVO' ? colors.warning : a.status === 'QUITADO' ? colors.success : colors.textDim} /></Td>
                  <Td>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                      <button onClick={() => openEdit(a)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.primary }}><Edit3 size={14} /></button>
                      <button onClick={() => setDeleteId(a.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.danger }}><Trash2 size={14} /></button>
                    </div>
                  </Td>
                </TrHover>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {/* Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}
        title={editId ? 'Editar Anuidade' : 'Registrar Anuidade'} width={700}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <FormField label="Cartão" required>
            <Select value={form.cartao_id} onChange={e => upd('cartao_id', e.target.value)} disabled={!!editId}>
              <option value="">Selecione o cartão...</option>
              {cartoes.map(c => (
                <option key={c.id} value={c.id}>{c.nome_cartao} ({c.titular_nome})</option>
              ))}
            </Select>
          </FormField>
          <FormField label="Isento">
            <Select value={form.isento} onChange={e => upd('isento', e.target.value)}>
              <option value="NÃO">NÃO</option>
              <option value="SIM">SIM</option>
            </Select>
          </FormField>
          {form.isento === 'SIM' ? (
            <FormField label="Motivo da Isenção" style={{ gridColumn: '1 / -1' }}>
              <Input value={form.motivo_isencao} onChange={e => upd('motivo_isencao', e.target.value)}
                placeholder="Ex: Negociado via SAC, programa de fidelidade..." />
            </FormField>
          ) : (
            <>
              <FormField label="Valor Total (R$)">
                <Input type="number" step="0.01" value={form.valor_total} onChange={e => calcParcela('valor_total', e.target.value)} placeholder="0.00" />
              </FormField>
              <FormField label="Número de Parcelas">
                <Input type="number" value={form.num_parcelas} onChange={e => calcParcela('num_parcelas', e.target.value)} min="1" placeholder="12" />
              </FormField>
              <FormField label="Valor da Parcela (R$)">
                <Input type="number" step="0.01" value={form.valor_parcela} onChange={e => upd('valor_parcela', e.target.value)} placeholder="Auto" />
              </FormField>
              <FormField label="Parcelas Pagas">
                <Input type="number" value={form.parcelas_pagas} onChange={e => upd('parcelas_pagas', e.target.value)} min="0" />
              </FormField>
              <FormField label="Data Início">
                <Input type="date" value={form.data_inicio} onChange={e => upd('data_inicio', e.target.value)} />
              </FormField>
              <FormField label="Data Fim">
                <Input type="date" value={form.data_fim} onChange={e => upd('data_fim', e.target.value)} />
              </FormField>
            </>
          )}
          <FormField label="Status">
            <Select value={form.status} onChange={e => upd('status', e.target.value)}>
              {STATUS_ANUIDADE.map(s => <option key={s} value={s}>{s}</option>)}
            </Select>
          </FormField>
        </div>
        <FormField label="Observações">
          <TextArea value={form.observacoes} onChange={e => upd('observacoes', e.target.value)} placeholder="Observações..." />
        </FormField>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <Button variant="outline" color={colors.textMuted} onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Salvando...' : editId ? 'Salvar' : 'Registrar Anuidade'}</Button>
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete}
        title="Excluir Anuidade" message="Tem certeza que deseja excluir este registro de anuidade?" />
    </div>
  );
}
